<template>
  <div>
    <div class="row">
      <div class="col-md-12">
        <card class="card">
          <h4 slot="header" class="card-title">Found Games</h4>

          <!-- Filters -->
          <div class="row mb-3">
            <div class="col-md-3">
              <base-input
                v-model="search"
                placeholder="Search team name..."
                @input="onSearchInput"
              />
            </div>
            <div class="col-md-2">
              <base-input>
                <select class="form-control my-select" v-model="statusFilter" @change="onFilterChange">
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="valid">Valid</option>
                  <option value="false_positive">False Positive</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input>
                <select class="form-control my-select" v-model="typeFilter" @change="onFilterChange">
                  <option value="">All types</option>
                  <option value="bookie_vs_provider">Bookie vs Provider</option>
                  <option value="bookie_vs_bookie">Bookie vs Bookie</option>
                  <option value="kick_off_change">Kick-off Change</option>
                  <option value="early_kick_off">Early Kick-off</option>
                  <option value="to_finish">To Finish</option>
                  <option value="postponed">Postponed</option>
                  <option value="game_found">Game Found</option>
                  <option value="awarded">Awarded</option>
                  <option value="bookie_diff">Bookie Diff</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-3">
              <base-input>
                <select class="form-control my-select" v-model="channelFilter" @change="onFilterChange" :disabled="loadingChannels">
                  <option value="">All channels</option>
                  <option
                    v-for="channel in channels"
                    :key="channel.telegram_chat_id"
                    :value="channel.telegram_chat_id"
                  >{{ channel.telegram_channel_name || channel.telegram_chat_id }}</option>
                </select>
              </base-input>
            </div>
            <div class="col-md-2 text-right">
              <button v-if="isSoundPlaying" class="btn btn-danger btn-sm stop-sound-btn mr-1" @click="stopSound">
                Stop Sound
              </button>
              <button
                class="btn btn-sm mr-1"
                :class="showOnlySubscribed ? 'btn-warning' : 'btn-default'"
                @click="showOnlySubscribed = !showOnlySubscribed"
                :title="showOnlySubscribed ? 'Showing only your alerts — click to show all' : 'Show only games you set alerts for'"
              >
                <i class="tim-icons icon-bell-55"></i>
                My Alerts
                <span v-if="subscribedCount > 0" class="alert-count-badge">{{ subscribedCount }}</span>
              </button>
              <button class="btn btn-primary btn-sm" @click="fetchGames" :disabled="refreshing">
                <i class="tim-icons icon-refresh-02" :class="{ 'spin': refreshing }"></i>
                Refresh
              </button>
            </div>
          </div>

          <!-- Kick-off alerts -->
          <div v-if="kickedOffAlerts.length > 0" class="kickoff-alerts mb-3">
            <div v-for="alert in kickedOffAlerts" :key="alert.gameId" class="kickoff-alert-item">
              <span class="kickoff-alert-icon">&#9917;</span>
              <span class="kickoff-alert-body">
                <strong>Kick-off reached:</strong> {{ alert.matchTitle }}
                <span class="kickoff-alert-time">· {{ alert.firedAt }}</span>
              </span>
              <button class="btn btn-sm kickoff-alert-scroll-btn" @click="scrollToGame(alert.gameId)">
                Scroll to row
              </button>
              <button class="kickoff-alert-dismiss" @click="dismissAlert(alert.gameId)">&times;</button>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loading" class="text-center py-4">
            <span>Loading...</span>
          </div>

          <!-- Table -->
          <div v-else class="table-responsive">
            <table class="table tablesorter">
              <thead class="text-primary">
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>Game 1</th>
                  <th>Game 2</th>
                  <th>Diff</th>
                  <th>Reviewed At</th>
                  <th>Found At</th>
                  <th>Status</th>
                  <th class="text-center">Alert</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="displayedGames.length === 0">
                  <td colspan="10" class="text-center">
                    {{ showOnlySubscribed ? 'No alert subscriptions on this page.' : 'No records found.' }}
                  </td>
                </tr>
                <tr
                  v-for="game in displayedGames"
                  :key="game.id"
                  :data-game-id="game.id"
                  :class="{ 'row-kicked-off': isKickedOff(game.id) }"
                >
                  <td>{{ game.id }}</td>
                  <td>
                    <span class="badge" :class="typeBadgeClass(game.type)">
                      {{ formatType(game.type) }}
                    </span>
                  </td>
                  <td>{{ game.telegram_channel_name || '—' }}</td>
                  <td>
                    <div v-if="game.game1">
                      <a v-if="game.game1.url" :href="game.game1.url" target="_blank" rel="noopener noreferrer">
                        <strong>{{ game.game1.home_team }} vs {{ game.game1.away_team }}</strong>
                      </a>
                      <strong v-else>{{ game.game1.home_team }} vs {{ game.game1.away_team }}</strong>
                      <br>
                      <span class="game-meta">{{ game.game1.bookie }} · {{ formatKickOff(game.game1.kick_off) }}</span>
                    </div>
                    <span v-else>—</span>
                  </td>
                  <td>
                    <div v-if="game.game2">
                      <a v-if="game.game2.url" :href="game.game2.url" target="_blank" rel="noopener noreferrer">
                        <strong>{{ game.game2.home_team }} vs {{ game.game2.away_team }}</strong>
                      </a>
                      <strong v-else>{{ game.game2.home_team }} vs {{ game.game2.away_team }}</strong>
                      <br>
                      <span class="game-meta">{{ game.game2.bookie }} · {{ formatKickOff(game.game2.kick_off) }}</span>
                    </div>
                    <span v-else>—</span>
                  </td>
                  <td>{{ game.time_difference_human !== null ? game.time_difference_human : '—' }}</td>
                  <td>{{ game.reviewed_at ? formatDateTime(game.reviewed_at) : '—' }}</td>
                  <td>{{ formatDateTime(game.created_at) }}</td>
                  <td>
                    <select
                      class="form-control my-select status-select"
                      :class="statusSelectClass(game.review_status)"
                      :value="game.review_status"
                      :disabled="updatingId === game.id"
                      @change="updateStatus(game, $event.target.value)"
                    >
                      <option value="pending">Pending</option>
                      <option value="valid">Valid</option>
                      <option value="false_positive">False Positive</option>
                    </select>
                  </td>
                  <td class="text-center">
                    <button
                      v-if="getLatestKickOff(game) > 0"
                      class="bell-btn"
                      :class="{ 'bell-active': isSubscribed(game.id) }"
                      @click="toggleSubscription(game)"
                      :title="isSubscribed(game.id) ? 'Cancel kick-off notification' : 'Notify me at kick-off'"
                    >
                      <i class="tim-icons icon-bell-55"></i>
                    </button>
                    <span v-else class="text-muted">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="row mt-3 align-items-center">
            <div class="col-md-4">
              <small class="text-muted">
                Showing {{ pagination.from }}–{{ pagination.to }} of {{ pagination.total }} records
              </small>
            </div>
            <div class="col-md-4 text-center">
              <button
                class="btn btn-sm btn-default"
                :disabled="pagination.current_page <= 1"
                @click="goToPage(pagination.current_page - 1)"
              >&laquo; Prev</button>
              <span class="mx-2" style="font-size: 13px;">
                Page {{ pagination.current_page }} / {{ pagination.last_page }}
              </span>
              <button
                class="btn btn-sm btn-default"
                :disabled="pagination.current_page >= pagination.last_page"
                @click="goToPage(pagination.current_page + 1)"
              >Next &raquo;</button>
            </div>
            <div class="col-md-4 text-right">
              <span style="font-size: 13px; margin-right: 8px;">Rows per page:</span>
              <select class="form-control my-select d-inline-block" style="width: auto;" v-model="perPage" @change="onFilterChange">
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <dashboard-section-links />
        </card>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import formatDateTime from "../mixin/formatDateTime";
import DashboardSectionLinks from "@/components/DashboardSectionLinks.vue";

const STORAGE_KEY = "foundgames_subscriptions";

export default {
  name: "FoundGames",
  components: {
    DashboardSectionLinks,
  },
  mixins: [formatDateTime],
  data() {
    return {
      games: [],
      channels: [],
      loading: false,
      refreshing: false,
      loadingChannels: false,
      updatingId: null,
      search: "",
      statusFilter: "",
      typeFilter: "",
      channelFilter: "",
      perPage: "25",
      searchTimeout: null,
      autoRefreshInterval: null,
      kickOffWatcherInterval: null,
      audioCtx: null,
      isSoundPlaying: false,
      activeOscillators: [],
      activeMasterGain: null,
      subscribedGames: {},
      kickedOffAlerts: [],
      showOnlySubscribed: false,
      pagination: {
        current_page: 1,
        last_page: 1,
        from: 0,
        to: 0,
        total: 0,
      },
    };
  },
  computed: {
    displayedGames() {
      if (!this.showOnlySubscribed) return this.games;
      return Object.values(this.subscribedGames)
        .filter((sub) => sub.game)
        .map((sub) => sub.game);
    },
    subscribedCount() {
      return Object.keys(this.subscribedGames).length;
    },
  },
  mounted() {
    this.loadSubscriptions();
    this.fetchChannels();
    this.fetchGames();
    this.scheduleAutoRefresh();
    this.startKickOffWatcher();
  },
  beforeDestroy() {
    clearTimeout(this.autoRefreshInterval);
    clearInterval(this.kickOffWatcherInterval);
    this.stopSound();
    if (this.audioCtx) this.audioCtx.close();
  },
  methods: {
    isSubscribed(gameId) {
      return !!this.subscribedGames[gameId];
    },
    getLatestKickOff(game) {
      const parse = (s) => s ? new Date(s.replace(" ", "T")).getTime() : 0;
      const t1 = game.game1 && game.game1.kick_off ? parse(game.game1.kick_off) : 0;
      const t2 = game.game2 && game.game2.kick_off ? parse(game.game2.kick_off) : 0;
      if (t1 === 0) return t2;
      if (t2 === 0) return t1;
      return Math.min(t1, t2);
    },
    getMatchTitle(game) {
      const g = game.game1 || game.game2;
      if (!g) return `Game #${game.id}`;
      return `${g.home_team} vs ${g.away_team}`;
    },
    toggleSubscription(game) {
      if (this.isSubscribed(game.id)) {
        this.$delete(this.subscribedGames, game.id);
        this.saveSubscriptions();
        return;
      }
      if (Notification.permission === "denied") {
        this.$toast.error("Browser notifications are blocked. Please allow them in your browser settings.");
        return;
      }
      // Warm up AudioContext on this user interaction so it stays unlocked for later playback
      this.initAudioCtx();
      const doSubscribe = () => {
        const kickOffTimestamp = this.getLatestKickOff(game);
        const matchTitle = this.getMatchTitle(game);
        this.$set(this.subscribedGames, game.id, { kickOffTimestamp, matchTitle, game });
        this.saveSubscriptions();
        const kickOffDate = new Date(kickOffTimestamp);
        const isToday = kickOffDate.toDateString() === new Date().toDateString();
        const timeStr = kickOffDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        const whenStr = isToday ? `at ${timeStr}` : `on ${kickOffDate.toLocaleDateString([], { month: "short", day: "numeric" })} at ${timeStr}`;
        this.$toast.success(`You will be notified ${whenStr} for ${matchTitle}`);
        // Fire immediately if kick-off has already passed
        this.checkKickOffs();
      };
      if (Notification.permission === "default") {
        Notification.requestPermission().then((permission) => {
          if (permission !== "granted") {
            this.$toast.error("Browser notifications are blocked. Please allow them in your browser settings.");
            return;
          }
          doSubscribe();
        });
      } else {
        doSubscribe();
      }
    },
    initAudioCtx() {
      try {
        if (!this.audioCtx) {
          this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        } else if (this.audioCtx.state === "suspended") {
          this.audioCtx.resume();
        }
      } catch (e) {
        // Web Audio API unavailable
      }
    },
    saveSubscriptions() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.subscribedGames));
      } catch (e) {
        // sessionStorage unavailable
      }
    },
    loadSubscriptions() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) this.subscribedGames = JSON.parse(raw);
      } catch (e) {
        this.subscribedGames = {};
      }
    },
    syncSubscriptions() {
      let changed = false;
      this.games.forEach((game) => {
        if (!this.subscribedGames[game.id]) return;
        const newTimestamp = this.getLatestKickOff(game);
        const newTitle = this.getMatchTitle(game);
        const sub = this.subscribedGames[game.id];
        if (newTimestamp !== sub.kickOffTimestamp || newTitle !== sub.matchTitle || !sub.game) {
          this.$set(this.subscribedGames, game.id, { kickOffTimestamp: newTimestamp, matchTitle: newTitle, game });
          changed = true;
        }
      });
      if (changed) this.saveSubscriptions();
    },
    startKickOffWatcher() {
      // Fallback watcher for when data refreshes fail
      this.kickOffWatcherInterval = setInterval(() => this.checkKickOffs(), 60 * 1000);
    },
    checkKickOffs() {
      const now = Date.now();
      let changed = false;
      Object.keys(this.subscribedGames).forEach((id) => {
        const sub = this.subscribedGames[id];
        if (sub.kickOffTimestamp > 0 && now >= sub.kickOffTimestamp) {
          this.fireKickOffNotification(sub, Number(id));
          this.$delete(this.subscribedGames, id);
          changed = true;
        }
      });
      if (changed) this.saveSubscriptions();
    },
    fireKickOffNotification(sub, gameId) {
      this.kickedOffAlerts.push({
        gameId,
        matchTitle: sub.matchTitle,
        firedAt: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      });
      this.playKickOffSound();
      if (Notification.permission === "granted") {
        new Notification(`Kick-off: ${sub.matchTitle}`, {
          body: "Kick-off time has been reached!",
        });
      }
    },
    dismissAlert(gameId) {
      const idx = this.kickedOffAlerts.findIndex((a) => a.gameId === gameId);
      if (idx !== -1) this.kickedOffAlerts.splice(idx, 1);
    },
    scrollToGame(gameId) {
      const row = document.querySelector(`tr[data-game-id="${gameId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      } else {
        this.$toast.info("This game is not on the current page.");
      }
    },
    isKickedOff(gameId) {
      return this.kickedOffAlerts.some((a) => a.gameId === gameId);
    },
    playKickOffSound() {
      try {
        const ctx = this.audioCtx;
        if (!ctx) return;
        const play = () => {
          const masterGain = ctx.createGain();
          masterGain.connect(ctx.destination);
          masterGain.gain.setValueAtTime(0.35, ctx.currentTime);
          this.activeMasterGain = masterGain;
          this.activeOscillators = [];

          // 9 rapid alternating beeps (~3.6s total)
          const BEEP_INTERVAL = 0.4;
          const BEEP_DURATION = 0.22;
          const alarmTones = [880, 1100];
          for (let i = 0; i < 9; i++) {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(masterGain);
            osc.type = "square";
            osc.frequency.value = alarmTones[i % 2];
            const start = ctx.currentTime + i * BEEP_INTERVAL;
            gain.gain.setValueAtTime(1, start);
            gain.gain.exponentialRampToValueAtTime(0.001, start + BEEP_DURATION);
            osc.start(start);
            osc.stop(start + BEEP_DURATION + 0.05);
            this.activeOscillators.push(osc);
          }

          this.isSoundPlaying = true;
          // Auto-clear after the last beep finishes
          setTimeout(() => this.stopSound(), (9 * BEEP_INTERVAL + 0.5) * 1000);
        };

        if (ctx.state === "suspended") {
          ctx.resume().then(play);
        } else {
          play();
        }
      } catch (e) {
        // Web Audio API unavailable
      }
    },
    stopSound() {
      if (this.activeMasterGain) {
        try { this.activeMasterGain.gain.setValueAtTime(0, this.audioCtx.currentTime); } catch (e) {}
      }
      this.activeOscillators.forEach((osc) => {
        try { osc.stop(this.audioCtx.currentTime); } catch (e) {}
      });
      this.activeOscillators = [];
      this.activeMasterGain = null;
      this.isSoundPlaying = false;
    },
    fetchChannels() {
      this.loadingChannels = true;
      axios
        .get("/potential-games/channels")
        .then((response) => {
          this.channels = response.data;
        })
        .catch(() => {
          // non-critical, silently ignore
        })
        .finally(() => {
          this.loadingChannels = false;
        });
    },
    scheduleAutoRefresh() {
      this.autoRefreshInterval = setTimeout(() => {
        this.fetchGames().finally(() => this.scheduleAutoRefresh());
      }, 10 * 1000);
    },
    fetchGames() {
      this.refreshing = true;
      if (this.games.length === 0) this.loading = true;
      const params = {
        page: this.pagination.current_page,
        per_page: this.perPage,
      };
      if (this.search) params.search = this.search;
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.typeFilter) params.type = this.typeFilter;
      if (this.channelFilter) params.telegram_chat_id = this.channelFilter;

      return axios
        .get("/potential-games", { params })
        .then((response) => {
          const data = response.data;
          this.games = data.data;
          this.pagination = {
            current_page: data.current_page,
            last_page: data.last_page,
            from: data.from || 0,
            to: data.to || 0,
            total: data.total,
          };
          this.syncSubscriptions();
          this.checkKickOffs();
        })
        .catch(() => {
          this.$toast.error("Failed to load Found Games.");
        })
        .finally(() => {
          this.loading = false;
          this.refreshing = false;
        });
    },
    updateStatus(game, newStatus) {
      if (newStatus === game.review_status) return;
      this.updatingId = game.id;
      axios
        .patch(`/potential-games/${game.id}`, { review_status: newStatus })
        .then((response) => {
          const index = this.games.findIndex((g) => g.id === game.id);
          if (index !== -1) {
            this.$set(this.games, index, response.data);
          }
          this.$toast.success(`Status updated to "${this.formatStatus(newStatus)}".`);
        })
        .catch(() => {
          this.$toast.error("Failed to update status.");
        })
        .finally(() => {
          this.updatingId = null;
        });
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.current_page = 1;
        this.fetchGames();
      }, 400);
    },
    onFilterChange() {
      this.pagination.current_page = 1;
      this.fetchGames();
    },
    goToPage(page) {
      this.pagination.current_page = page;
      this.fetchGames();
    },
    formatType(type) {
      const map = {
        bookie_vs_provider: "Bookie vs Provider",
        bookie_vs_bookie: "Bookie vs Bookie",
        kick_off_change: "KO Change",
        early_kick_off: "Early KO",
        to_finish: "To Finish",
        postponed: "Postponed",
        game_found: "Game Found",
        awarded: "Awarded",
        bookie_diff: "Bookie Diff",
      };
      return map[type] || type;
    },
    formatStatus(status) {
      const map = {
        pending: "Pending",
        valid: "Valid",
        false_positive: "False Positive",
      };
      return map[status] || status;
    },
    formatKickOff(kickOff) {
      if (!kickOff) return "—";
      return kickOff.substring(0, 16);
    },
    typeBadgeClass(type) {
      const map = {
        bookie_vs_provider: "badge-info",
        bookie_vs_bookie: "badge-primary",
        kick_off_change: "badge-warning",
        early_kick_off: "badge-warning",
        to_finish: "badge-secondary",
        postponed: "badge-danger",
        game_found: "badge-success",
        awarded: "badge-light",
        bookie_diff: "badge-dark",
      };
      return map[type] || "badge-secondary";
    },
    statusSelectClass(status) {
      const map = {
        pending: "status-pending",
        valid: "status-valid",
        false_positive: "status-false-positive",
      };
      return map[status] || "";
    },
  },
};
</script>

<style scoped>
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  display: inline-block;
}
.badge-info { background-color: #17a2b8; color: #fff; }
.badge-primary { background-color: #007bff; color: #fff; }
.badge-warning { background-color: #ffc107; color: #212529; }
.badge-danger { background-color: #dc3545; color: #fff; }
.badge-success { background-color: #28a745; color: #fff; }
.badge-secondary { background-color: #6c757d; color: #fff; }
.badge-light { background-color: #f8f9fa; color: #212529; }
.badge-dark { background-color: #343a40; color: #fff; }
.d-flex { display: flex; }
.status-select {
  font-size: 12px;
  padding: 3px 6px;
  height: auto;
  min-width: 120px;
  font-weight: 600;
  border-radius: 4px;
}
.status-pending  { border-color: #b8960c; color: #b8960c; }
.game-meta { font-size: 13px; color: #adb5bd; }
.status-valid    { border-color: #28a745; color: #28a745; }
.status-false-positive { border-color: #dc3545; color: #dc3545; }
.spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.bell-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  color: #6c757d;
  font-size: 16px;
  line-height: 1;
  transition: color 0.2s, background 0.2s;
}
.bell-btn:hover {
  background: rgba(255, 193, 7, 0.15);
  color: #ffc107;
}
.bell-active {
  color: #ffc107 !important;
}
.kickoff-alerts {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.kickoff-alert-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(40, 167, 69, 0.1);
  border: 1px solid rgba(40, 167, 69, 0.35);
  border-left: 4px solid #28a745;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
}
.kickoff-alert-icon {
  font-size: 16px;
  flex-shrink: 0;
}
.kickoff-alert-body {
  flex: 1;
  color: #e9ecef;
}
.kickoff-alert-time {
  color: #adb5bd;
  font-size: 12px;
}
.kickoff-alert-scroll-btn {
  flex-shrink: 0;
  font-size: 12px;
  padding: 2px 10px;
  border: 1px solid #28a745;
  color: #28a745;
  background: transparent;
  border-radius: 4px;
}
.kickoff-alert-scroll-btn:hover {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}
.kickoff-alert-dismiss {
  background: none;
  border: none;
  color: #adb5bd;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
  flex-shrink: 0;
}
.kickoff-alert-dismiss:hover {
  color: #dc3545;
}
.row-kicked-off {
  background-color: rgba(40, 167, 69, 0.07) !important;
  box-shadow: inset 3px 0 0 #28a745;
}
.alert-count-badge {
  display: inline-block;
  background: #dc3545;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 5px;
  margin-left: 3px;
  vertical-align: middle;
  line-height: 1.4;
}
.stop-sound-btn {
  animation: pulse-red 0.8s ease-in-out infinite;
}
@keyframes pulse-red {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
</style>
